/**
 * AppContext.tsx
 * Contexto global mínimo: tema + auth.
 * Permite que HeaderSection viva en App.tsx (Layout persistente)
 * sin que el header se desmonte al navegar entre rutas.
 *
 * @task UI-HEADER-03 v3 — fix definitivo del salto visual
 */
import { createContext, useContext, useState, useCallback, useEffect, type FC, type ReactNode } from 'react';

type Theme = 'dark' | 'light';

const THEME_KEY    = 'ptel_theme';

// ── Tipos ─────────────────────────────────────────────────────────────────────

interface AppContextValue {
  /** Tema activo */
  theme: Theme;
  /** Alterna claro / oscuro */
  toggleTheme: () => void;
  /** Fuerza un tema concreto (lo usa HomePage al arrancar en dark) */
  setTheme: (t: Theme) => void;
  /** Estado de autenticación */
  isAuthenticated: boolean;
  username?: string;
  /** Actualiza auth desde HomePage tras login/logout */
  notifyAuth: (isAuth: boolean, user?: string) => void;
  /** Dispatcher para abrir el modal de login (registrado por HomePage) */
  openLoginModal: () => void;
  /** Registra el callback del modal (llamado desde HomePage) */
  registerLoginModal: (fn: () => void) => void;
  /** Dispatcher de logout (registrado por HomePage) */
  handleLogout: () => void;
  /** Registra el callback de logout */
  registerLogout: (fn: () => void) => void;
}

// ── Contexto ──────────────────────────────────────────────────────────────────

const AppContext = createContext<AppContextValue>(null!);

export const useAppContext = () => useContext(AppContext);

// ── Provider ──────────────────────────────────────────────────────────────────

export const AppProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // Tema: inicializa desde localStorage; fallback dark (marca PTEL)
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem(THEME_KEY);
    return saved === 'light' ? 'light' : 'dark';
  });

  // Auth: arranca en false; se sincroniza desde AppShell via notifyAuth()
  // cuando Firebase resuelve onAuthStateChanged (token persistente o login nuevo).
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [username, setUsername] = useState<string | undefined>(undefined);

  // Callbacks registrados por HomePage
  const [loginModalFn, setLoginModalFn] = useState<() => void>(() => () => {});
  const [logoutFn, setLogoutFn]         = useState<() => void>(() => () => {});

  // ── Tema ────────────────────────────────────────────────────────────────────

  // Sincronizar DOM con el tema inicial al montar el Provider
  // (sin esto, el atributo data-theme nunca se aplica en la primera carga)
  useEffect(() => {
    if (theme === 'light') {
      document.documentElement.setAttribute('data-theme', 'light');
    }
    // Si es dark (por defecto), no hace falta atributo: home-v2.css asume dark sin él
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    if (t === 'light') {
      document.documentElement.setAttribute('data-theme', 'light');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    // Guardamos siempre para que la preferencia persista entre páginas
    localStorage.setItem(THEME_KEY, t);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem(THEME_KEY, next);
      return next;
    });
  }, []);

  // ── Auth ────────────────────────────────────────────────────────────────────

  const notifyAuth = useCallback((isAuth: boolean, user?: string) => {
    setIsAuthenticated(isAuth);
    setUsername(user);
  }, []);

  const openLoginModal  = useCallback(() => loginModalFn(), [loginModalFn]);
  const handleLogout    = useCallback(() => logoutFn(),     [logoutFn]);

  const registerLoginModal = useCallback((fn: () => void) => {
    setLoginModalFn(() => fn);
  }, []);

  const registerLogout = useCallback((fn: () => void) => {
    setLogoutFn(() => fn);
  }, []);

  return (
    <AppContext.Provider value={{
      theme, toggleTheme, setTheme,
      isAuthenticated, username, notifyAuth,
      openLoginModal, registerLoginModal,
      handleLogout, registerLogout,
    }}>
      {children}
    </AppContext.Provider>
  );
};
